import { Logout, Password, Person, SwitchAccount } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  logout,
  stopImpersonation,
} from "../../../application/actions/authActions";
import { getAuthSlice } from "../../../application/selectors/auth";
import {
  IMPERSONATION_RETURN_PATH,
  isImpersonating,
} from "../../../infrastructure/utils/impersonationSession";
import LogoIcon from "../../assets/images/logo-icon.png";
import IconComponent from "../shared/icon";
import AlarmDrawer from "./AlarmDrawer";
import HeaderFinanceMenu from "../customer/HeaderFinanceMenu";
import HistoryDrawer from "./HistoryDrawer";
import TicketDrawer from "./TicketDrawer";
import MobileSidebar from "./MobileSideBar";

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: "4rem",
  display: "flex",
  justifyContent: "center",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const drawerWidth = 240;

const getUserDisplayName = (user) => {
  if (!user) return "";
  const first = String(user.firstName ?? user.FirstName ?? "").trim();
  const last = String(user.lastName ?? user.LastName ?? "").trim();
  const full = String(user.fullName ?? user.FullName ?? "").trim();
  if (first && last) return `${first} ${last}`;
  if (full) return full;
  return first || last || "";
};

const AppBarContainer = ({
  open,
  handleDrawerClose,
  handleDrawerOpen,
  isShowMainMenu,
}) => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  let location = useLocation();
  const { user: currentUser } = useSelector(getAuthSlice);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const onLogoutUserAccount = () => {
    setAnchorElUser(null);
    dispatch(logout());
    navigate("/auth/login");
  };

  const onResetPassword = () => {
    handleCloseUserMenu();
    navigate("/account/resetpassword");
  };

  const onShowProfile = () => {
    handleCloseUserMenu();
    navigate("/account/profile");
  };

  const onStopImpersonation = async () => {
    try {
      await dispatch(stopImpersonation()).unwrap();
      window.location.replace(IMPERSONATION_RETURN_PATH);
    } catch {
      navigate("/");
    }
  };

  const impersonating = isImpersonating();

  if (!currentUser) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  const defaultUrl =
    currentUser?.defaultUrl || window.localStorage.getItem("defaultUrl") || "";
  const showCustomerFinanceMenu =
    location.pathname.startsWith("/SLN/Customer") ||
    String(defaultUrl).includes("/SLN/Customer");

  const userDisplayName = getUserDisplayName(currentUser);

  return (
    <>
      <AppBar position="fixed" color="secondary" className="app-bar-container">
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            className="desktop-icon"
            id="side-bar-icon"
            sx={{
              mr: 3,
              // display: { xs: "none", md: "block" },
            }}
          >
            {isShowMainMenu ? (
              open ? (
                <MenuOpenIcon
                  sx={{ pointerEvents: "none", transform: "rotate(180deg)" }}
                />
              ) : (
                <MenuIcon sx={{ pointerEvents: "none" }} />
              )
            ) : null}
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
            className="mobile-icon"
            data-testid="mobile-menu-button"
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
            }}
          >
            <img src={LogoIcon} alt="PMS" width={32} height={32} />
            سولارنت
            {currentUser?.isTrialVersion ? (
              <Typography color={"secondary"} variant="body1" fontWeight={700}>
                &nbsp; (نسخه آزمایشی)
              </Typography>
            ) : null}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {impersonating ? (
              <Tooltip title="بازگشت به حساب اصلی">
                <IconButton
                  color="warning"
                  onClick={onStopImpersonation}
                  aria-label="بازگشت به حساب اصلی"
                  sx={{ mr: 0.5 }}
                >
                  <SwitchAccount />
                </IconButton>
              </Tooltip>
            ) : null}

            {showCustomerFinanceMenu ? <HeaderFinanceMenu /> : null}

            <HistoryDrawer />

            <TicketDrawer />

            <AlarmDrawer />

            <Button
              onClick={handleOpenUserMenu}
              id="user-menu-app-bar"
              className="user-menu-button-container"
              sx={{ textTransform: "none", minWidth: 0 }}
            >
              {userDisplayName ? (
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    overflow: { xs: "hidden", md: "visible" },
                    textOverflow: { xs: "ellipsis", md: "clip" },
                    whiteSpace: "nowrap",
                    maxWidth: { xs: 90, sm: 140, md: "none" },
                  }}
                  title={userDisplayName}
                >
                  {userDisplayName}
                </Typography>
              ) : null}
              <div className="avatar">
                <IconComponent name="user" width="16px" height="16px" />
              </div>
            </Button>

            <Menu
              id="user-menu-app-bar"
              anchorEl={anchorElUser}
              keepMounted
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={onShowProfile}>
                <ListItemIcon>
                  <Person fontSize="small" />
                </ListItemIcon>
                پروفایل
              </MenuItem>

              {/* <MenuItem onClick={handleCloseUserMenu}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                تنظیمات
              </MenuItem> */}

              <MenuItem onClick={onResetPassword}>
                <ListItemIcon>
                  <Password fontSize="small" />
                </ListItemIcon>
                تغییر کلمه عبور
              </MenuItem>

              <Divider />

              <MenuItem onClick={onLogoutUserAccount} color="primary">
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                خروج
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <MobileSidebar
        open={mobileOpen}
        onClose={(event) => {
          if (event) {
            setMobileOpen((prevState) => false);
          }
        }}
      />
    </>
  );
};

export default AppBarContainer;
